import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Box from "../components/Box/Box";
import { formDataAtom } from "../App";
import axios from "axios";
import Progressbar from "../components/Progressbar/Progressbar";
import { WITH_APP, WITH_BLIP, WITH_OTHER, WITH_TICKET, WITH_TRAVELCARD } from "./PaymentMethod";
import { COMPENSATION_TICKET, COMPENSATION_TAXI, COMPENSATION_CAR } from "./Compensation";
import { VALUE_CODE, BANKDEPOSIT } from "./RefundPreference";
import { DELAY_20_39_MINUTES, DELAY_40_59_MINUTES, DELAY_OVER_60_MINUTES } from "./Delay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useRef, useState } from "react";
import Checkbox from "../components/Form/Checkbox";
import BackButton from "../components/Buttons/BackButton";
import ErrorList from "../components/Form/ErrorList";
import { handleSubmitClick } from "../components/Form/FormUtils";
import Spinner from "../components/Spinner/Spinner";
import useFocusOnLoad from "../hooks/useFocusOnLoad";

interface SummaryModel {
    gdprAccept: boolean;
}
interface Issue {
    id: string;
}

export const issueAtom = atom<Issue>({
    id: "",
});

export default function Summary() {
    const [formData, setFormData] = useAtom(formDataAtom);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
    const navigate = useNavigate();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setIssueId] = useAtom(issueAtom);
    const formRef = useRef<HTMLFormElement>(null);

    function GetEditButtonForNavigation(path: string, text?: string) {
        return (
            <button
                type="button"
                className="btn stripped icon-link p-0 justify-self-end change-link"
                onClick={() => navigate(path)}
            >
                <FontAwesomeIcon
                    className="text-xl mr-2 relative top-0.5 text-r"
                    icon={solid("edit")}
                />
                {text ?? "Gå tillbaka och ändra"}
            </button>
        );
    }

    return (
        <div>
            <Formik<SummaryModel>
                onSubmit={(values) => {
                    const emailContact = formData.emailcontact;
                    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

                    window.scrollTo({
                        top: 0,
                        behavior: "smooth", // Smooth scrolling
                    });

                    setIsFormSubmitted(true);
                    axios
                        .post(
                            `${apiBaseUrl?.replace(/\/$/, '') ?? ''}/crm/compensation`,
                            formData,
                            {
                                headers: {
                                    "referrer-policy": "strict-origin",
                                },
                            }
                        )
                        .then((result) => {
                            setIssueId({ id: result.data });
                            setFormData({
                                paymentmethod: "",
                                phonenumberapp: "",
                                ticketnumber: "",
                                cardnumber: "",
                                delay: "",
                                compensation: "",
                                refundpreference: "",
                                taxireceipt: [],
                                distance: "",
                                cartravelfrom: "",
                                cartravelto: "",
                                carlicenceplate: "",
                                carexpenses: [],
                                valuecodeemail: "",
                                clearingnumber: "",
                                accountnumber: "",
                                firstname: "",
                                lastname: "",
                                streetaddress: "",
                                zipcode: "",
                                postaladdress: "",
                                phonenumbercontact: "",
                                emailcontact: "",
                                moreinfo: "",
                                journey: "",
                                selectedJourney: { fromName: '', toName: '', date: '', travelTime: '', journeyKey: '', line: '', switches: '', unformattedDate: '' },
                            });

                            navigate("/tack-sida", { state: { emailContact } });
                        })
                        .catch((error) => {
                            navigate("../ett-fel-intraffade");
                        });
                }}
                initialValues={{
                    gdprAccept: false,
                }}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={Yup.object().shape({
                    gdprAccept: Yup.bool().test(
                        "checkbox-selected",
                        "Du måste godkänna hanteringen av dina personuppgifter.",
                        (value) => {
                            return value === true;
                        }
                    ),
                    // ...formValidation(formData),
                })}
                enableReinitialize
            >
                {({ errors, values }) => (
                    <>
                        <div className="flex justify-between items-center">
                            <div className="flex justify-center h-15 md:h-auto mb-1">
                                <div
                                    className={`w-full text-left md:hidden ${isFormSubmitted === true ? "hide" : ""
                                        }`}
                                >
                                </div>
                            </div>

                            <div className="xs:w-52 md:w-full max-w-xs md:mb-4">
                                <Progressbar percentage={"100"} />
                            </div>
                        </div>
                        <Form>


                            <Box fullwidth>
                                {isFormSubmitted && <Spinner label="Skickar ..." />}
                                <div
                                    className={`inner-summary ${isFormSubmitted === true ? "hide" : ""
                                        }`}
                                >
                                    <h2 ref={h2Ref} tabIndex={-1}>
                                        Granska och skicka in ansökan
                                    </h2>

                                    {Object.keys(errors).length > 0 && <ErrorList errors={errors} />}

                                    <p>
                                        Vänligen granska uppgifterna innan du skickar in ansökan.
                                        Vill du ändra något klickar du på "Gå tillbaka och ändra".
                                        När du är färdig klickar du på ”Skicka ansökan”.
                                    </p>

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Hur såg din planerade resa ut?</h3>

                                        <div className="summary-data-wrapper">
                                            <div>
                                                <div className="">
                                                    <span className="summary-data-label">Från:</span>
                                                    <span className="summary-data">{formData.selectedJourney.fromName}</span>
                                                </div>
                                                <div className="">
                                                    <span className="summary-data-label">Till:</span>
                                                    <span className="summary-data">{formData.selectedJourney.toName}</span>
                                                </div>
                                                <div className="">
                                                    <span className="summary-data-label last">Datum för resa:</span>
                                                    <span className="summary-data last">{formData.selectedJourney.date}</span>
                                                </div>
                                                <div className="">
                                                    <span className="summary-data-label">Avgång- och ankomsttid:</span>
                                                    <span className="summary-data">{formData.selectedJourney.travelTime}</span>
                                                </div>
                                            </div>

                                            <div className="summary-edit">
                                                {GetEditButtonForNavigation("../hur-du-reste")}
                                            </div>
                                        </div>

                                    </div>


                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Hur stor blev förseningen?</h3>

                                        <div className="summary-data-wrapper">
                                            <div className="">
                                                <span className="summary-data last">
                                                    Jag blev {GetDelayReadable(formData.delay)}
                                                </span>
                                            </div>
                                            <div className="summary-edit">
                                                {GetEditButtonForNavigation("../hur-sen-blev-du")}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Var hade du din biljett?</h3>
                                        <div className="summary-data-wrapper">
                                            <div className="">
                                                <span className="summary-data last">{GetWayOfTravelReadable(formData.paymentmethod)}</span>
                                            </div>
                                            <div className="summary-edit">
                                                {GetEditButtonForNavigation("../betalningssatt")}
                                            </div>
                                        </div>
                                    </div>

                                    {formData.paymentmethod === WITH_APP || formData.paymentmethod === WITH_TRAVELCARD || formData.paymentmethod === WITH_BLIP || formData.paymentmethod === WITH_TICKET ? (

                                        <div className="summary-section patient-summary">
                                            <h3 className="summary-section-heading">Biljettinformation</h3>
                                            <div className="summary-data-wrapper">

                                                <div className="">
                                                    {formData.paymentmethod === WITH_APP ? (
                                                        <>
                                                            <div>
                                                                <span className="summary-data-label">Telefonnummer:</span>
                                                                <span className="summary-data">{formData.phonenumberapp}</span>
                                                            </div>
                                                            <div>
                                                                <span className="summary-data-label last">Biljett-ID:</span>
                                                                <span className="summary-data last">{formData.ticketnumber}</span>
                                                            </div>
                                                        </>
                                                    ) : formData.paymentmethod === WITH_TRAVELCARD ? (
                                                        <>
                                                            <div>
                                                                <span className="summary-data-label last">Reskortsnummer:</span>
                                                                <span className="summary-data last">{formData.cardnumber}</span>
                                                            </div>
                                                        </>
                                                    ) : formData.paymentmethod === WITH_BLIP || formData.paymentmethod === WITH_TICKET ? (
                                                        <>
                                                            <div>
                                                                <span className="summary-data-label last">Biljett-ID:</span>
                                                                <span className="summary-data last">{formData.ticketnumber}</span>
                                                            </div>
                                                        </>
                                                    ) : null}
                                                </div>

                                                <div className="summary-edit">
                                                    {formData.paymentmethod === WITH_APP ? (
                                                        <>
                                                            {GetEditButtonForNavigation("../med-app")}
                                                        </>
                                                    ) : formData.paymentmethod === WITH_TRAVELCARD ? (
                                                        <>
                                                            {GetEditButtonForNavigation("../med-resekort")}
                                                        </>
                                                    ) : formData.paymentmethod === WITH_BLIP ? (
                                                        <>
                                                            {GetEditButtonForNavigation("../med-blipp")}
                                                        </>
                                                    ) : formData.paymentmethod === WITH_TICKET ? (
                                                        <>
                                                            {GetEditButtonForNavigation("../med-biljett")}
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}


                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Vad önskar du ersättning för?</h3>
                                        <div className="summary-data-wrapper">

                                            <div className="">
                                                <span className="summary-data last">Jag vill ha ersättning {GetCompensationReadable(formData.compensation)}</span>
                                            </div>

                                            <div className="summary-edit">
                                                {GetEditButtonForNavigation("../ersattning-for")}
                                            </div>
                                        </div>
                                    </div>


                                    {formData.compensation === COMPENSATION_TAXI || formData.compensation === COMPENSATION_CAR ? (
                                        <div className="summary-section patient-summary">
                                            <h3 className="summary-section-heading">Uppgifter om ersättning</h3>

                                            <div className="summary-data-wrapper">
                                                <div>

                                                    {formData.compensation === COMPENSATION_TAXI ? (
                                                        <>
                                                            <div>
                                                                <span className="summary-data-label last">Bifogat kvitto:</span>
                                                                <span className="summary-data last"> 
                                                                    {formData.taxireceipt.map((doc) => (
                                                                        <div key={doc.file.name}>{doc.file.name}</div>
                                                                    ))}
                                                                </span>
                                                            </div>
                                                        </>
                                                    ) : formData.compensation === COMPENSATION_CAR ? (
                                                        <>
                                                            <div>
                                                                <span className="summary-data-label">Åkte från:</span>
                                                                <span className="summary-data">{formData.cartravelfrom}</span>
                                                            </div>

                                                            <div>
                                                                <span className={`summary-data-label ${formData.carexpenses.length < 1 ? "last" : ""}`}>Åkte till:</span>
                                                                <span className={`summary-data ${formData.carexpenses.length < 1 ? "last" : ""}`}>{formData.cartravelto}</span>
                                                            </div>


                                                            <div>
                                                                <span className="summary-data-label">Antal mil:</span>
                                                                <span className="summary-data">{formData.distance}</span>
                                                            </div>

                                                            <div>
                                                                <span className="summary-data-label">Registreringsnummer:</span>
                                                                <span className="summary-data uppercase">{formData.carlicenceplate}</span>
                                                            </div>

                                                            {formData.carexpenses.length > 0 && (
                                                                <>
                                                                    <div>
                                                                        <span className="summary-data-label last">Bifogat kvitto:</span>
                                                                        <span className="summary-data last">
                                                                            {formData.carexpenses.map((doc) => (
                                                                                <div key={doc.file.name}>{doc.file.name} </div>
                                                                            ))}
                                                                        </span>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </>

                                                    ) : null}
                                                </div>
                                                <div className="summary-edit">
                                                    {formData.compensation === COMPENSATION_TAXI ? (
                                                        <>
                                                            {GetEditButtonForNavigation("../ersattning-for-taxi")}
                                                        </>
                                                    ) : formData.compensation === COMPENSATION_CAR ? (
                                                        <>
                                                            {GetEditButtonForNavigation("../ersattning-for-bil")}
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}


                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Hur vill du ha ersättningen utbetald?</h3>
                                        <div className="summary-data-wrapper">
                                            <div className="">
                                                <span className="summary-data last">{GetRefundPreference(formData.refundpreference)}</span>
                                            </div>
                                            <div className="summary-edit">
                                                {GetEditButtonForNavigation("../hur-vill-du-ha-ersattning")}
                                            </div>
                                        </div>
                                    </div>

                                    {formData.refundpreference === VALUE_CODE || formData.refundpreference === BANKDEPOSIT ? (
                                        <div className="summary-section patient-summary">
                                            {formData.refundpreference === VALUE_CODE ? (
                                                <>
                                                    <h3 className="summary-section-heading">Värdekod</h3>
                                                    <div className="summary-data-wrapper">
                                                        <div>
                                                            <div className="">
                                                                <span className="summary-data-label last">Mejladress för värdekod:</span>
                                                                <span className="summary-data last">{formData.valuecodeemail}</span>
                                                            </div>
                                                        </div>
                                                        <div className="summary-edit">
                                                            {GetEditButtonForNavigation("../vardekod")}
                                                        </div>
                                                    </div>
                                                </>
                                            ) : formData.refundpreference === BANKDEPOSIT ? (
                                                <>
                                                    <h3 className="summary-section-heading">Insättning på bankkonto</h3>
                                                    <div className="summary-data-wrapper">
                                                        <div>
                                                            <div className="">
                                                                <span className="summary-data-label">Clearingnummer:</span>
                                                                <span className="summary-data">{formData.clearingnumber}</span>
                                                            </div>
                                                            <div className="">
                                                                <span className="summary-data-label last">Kontonummer:</span>
                                                                <span className="summary-data last">{formData.accountnumber}</span>
                                                            </div>
                                                        </div>
                                                        <div className="summary-edit">
                                                            {GetEditButtonForNavigation("../bankinsattning")}
                                                        </div>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    <div className="summary-section patient-summary">
                                        <h3 className="summary-section-heading">Personuppgifter</h3>
                                        <div className="summary-data-wrapper">
                                            <div>
                                                <div className="">
                                                    <span className="summary-data-label">Förnamn:</span>
                                                    <span className="summary-data">{formData.firstname}</span>
                                                </div>
                                                <div className="">
                                                    <span className="summary-data-label">Efternamn:</span>
                                                    <span className="summary-data">{formData.lastname}</span>
                                                </div>

                                                <div className="">
                                                    <span className="summary-data-label">Gatuadress:</span>
                                                    <span className="summary-data">{formData.streetaddress}</span>
                                                </div>

                                                <div className="">
                                                    <span className="summary-data-label">Postnummer:</span>
                                                    <span className="summary-data">{formData.zipcode}</span>
                                                </div>

                                                <div className="">
                                                    <span className="summary-data-label">Postort:</span>
                                                    <span className="summary-data">{formData.postaladdress}</span>
                                                </div>

                                                <div className="">
                                                    <span className="summary-data-label">Telefonnummer för kontakt:</span>
                                                    <span className="summary-data">{formData.phonenumbercontact}</span>
                                                </div>
                                                <div className="">
                                                    <span className="summary-data-label">Mejladress för kontakt:</span>
                                                    <span className="summary-data">{formData.emailcontact}</span>
                                                </div>
                                                {formData.moreinfo && (
                                                    <div className="">
                                                        <span className="summary-data-label last">Information om resan:</span>
                                                        <span className="summary-data last">{formData.moreinfo}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="summary-edit">
                                                {GetEditButtonForNavigation("../personuppgifter")}
                                            </div>
                                        </div>


                                    </div>

                                    <div className="mt-6 mb-6 md:mb-8">
                                        <Checkbox name="gdprAccept">
                                            Jag godkänner att Östgötatrafiken sparar och hanterar mina
                                            personuppgifter enligt{" "}
                                            <Link
                                                target="_blank"
                                                className="red-link underline hover:no-underline"
                                                to="https://www.ostgotatrafiken.se/info/integritetspolicy-for-ostgotatrafikens-kunder/"
                                            >
                                                relevant dataskyddslagstiftning.
                                                <FontAwesomeIcon
                                                    icon={solid("external-link")}
                                                    className="ml-2"
                                                />
                                            </Link>
                                        </Checkbox>
                                    </div>
                                </div>

                            </Box>

                            <div
                                className={`flex justify-centery ${isFormSubmitted === true ? "hide" : ""
                                    }`}
                            >
                                <div className="w-full text-left flex items-center">
                                    <BackButton />
                                </div>
                                <div className="w-full text-right">
                                    <button
                                        onClick={handleSubmitClick(formRef)}
                                        className="btn primary"
                                        type="submit"
                                    >
                                        Skicka ansökan
                                    </button>
                                </div>
                            </div>
                        </Form>
                    </>
                )}
            </Formik>
        </div>
    );
}

function GetWayOfTravelReadable(PaymentMethod: string): string {
    return PaymentMethod === WITH_APP ? "I appen" :
        PaymentMethod === WITH_TRAVELCARD ? "På ett reskort" :
            PaymentMethod === WITH_BLIP ? "Blippat bankkort ombord" :
                PaymentMethod === WITH_TICKET ? "Pappersbiljett" :
                    PaymentMethod === WITH_OTHER ? "Jag hade ingen biljett" :
                        "";
}

function GetCompensationReadable(compensation: string): string {
    return compensation === COMPENSATION_TICKET ? "av priset för en enkelbiljett" :
        compensation === COMPENSATION_TAXI ? "för resa med taxi" :
            compensation === COMPENSATION_CAR ? "för resa med bil" :
                "";
}

function GetRefundPreference(refundpreference: string): string {
    return refundpreference === VALUE_CODE ? "Jag vill ha ersättning som en värdekod" :
        refundpreference === BANKDEPOSIT ? "Jag vill ha ersättning till mitt bankkonto" :
            "";
}

function GetDelayReadable(delay: string): string {
    return delay === DELAY_20_39_MINUTES ? "20 - 39 minuter försenad" :
        delay === DELAY_40_59_MINUTES ? "40 - 59 minuter försenad" :
            delay === DELAY_OVER_60_MINUTES ? "mer än 60 minuter försenad" :
                "";
}

// function formValidation(data: FormModel): any {
//     let valObj = {
//         paymentwithapp: Yup.bool().test(
//             "form-filled-out",
//             "Det saknas obligatoriska uppgifter under betalning med app.",
//             () => {
//                 return isAppPaymentFilledOut(data);
//             }
//         ),
//         paymentwithtravelcard: Yup.bool().test(
//             "form-filled-out",
//             "Det saknas obligatoriska uppgifter under betalning med reskort.",
//             () => {
//                 return isTravelcardPaymentFilledOut(data);
//             }
//         ),
//     } as any;
//     return valObj;
// }

// function isAppPaymentFilledOut(data: FormModel): boolean {

//     if (data.paymentmethod === WITH_APP) {
//         return (
//             (data.phonenumberapp != "" || data.ticketnumber != "")
//         );
//     }
//     else if (data.paymentmethod === WITH_TRAVELCARD) {
//         return (
//             (data.cardnumber != "")
//         );
//     } else {
//         return(true);
//     }
// }

